import { defineMessages } from '@faceup/localization'
import {
  CaseDeadlineFilter,
  CaseJustification,
  CasePriority,
  CaseSourceType,
  CaseStatusGroup,
  Day,
  type FrontendPermissionType,
} from '@faceup/utils'

// Enum-derived keys keep their PascalCase enum values as leaf ids so
// `xMessages[enumValue]` lookups stay enum-typed. Phase 3 (ENG-2030) lowercases
// all enum maps symmetrically.
const idToMessage = <S extends string>(id: S) => ({ id, defaultMessage: '' })

export const sharedMessages = defineMessages('institution.shared', [
  { id: 'functionNotReady', defaultMessage: '' },
  { id: 'apiError', defaultMessage: '' },
  { id: 'savedMessage', defaultMessage: '' },
  { id: 'encryptionError', defaultMessage: '' },
  { id: 'attachmentsTooManyFiles', defaultMessage: '' },
  { id: 'attachmentsTooLargeFile', defaultMessage: '' },
  { id: 'attachmentsLocked', defaultMessage: '' },
  { id: 'metadataRemoved', defaultMessage: '' },
  { id: 'metadataUnableToRemove', defaultMessage: '' },
  { id: 'unsupportedMimetype', defaultMessage: '' },
  { id: 'upgradeAlert', defaultMessage: '' },
  { id: 'upgradeButton', defaultMessage: '' },
  { id: 'permissionEditableOnlyMotherTooltip', defaultMessage: '' },
  { id: 'invalidNameError', defaultMessage: '' },
  { id: 'invalidEmailError', defaultMessage: '' },
  { id: 'invalidPhoneError', defaultMessage: '' },
  { id: 'invalidPasswordError', defaultMessage: '' },
  { id: 'invalidAddressError', defaultMessage: '' },
  { id: 'passwordsDontMatchError', defaultMessage: '' },
  { id: 'invalidInputError', defaultMessage: '' },
  { id: 'emailLabel', defaultMessage: '' },
  { id: 'nameLabel', defaultMessage: '' },
  { id: 'bioLabel', defaultMessage: '' },
  { id: 'fullNameLabel', defaultMessage: '' },
  { id: 'phoneLabel', defaultMessage: '' },
  { id: 'passwordLabel', defaultMessage: '' },
  { id: 'organizationLabel', defaultMessage: '' },
  { id: 'categoryLabel', defaultMessage: '' },
  { id: 'moreInfoLabel', defaultMessage: '' },
  { id: 'classNameLabel', defaultMessage: '' },
  { id: 'globalNameLabel', defaultMessage: '' },
  { id: 'bio', defaultMessage: '' },
  { id: 'partnerBio', defaultMessage: '' },
  { id: 'send', defaultMessage: '' },
  { id: 'delete', defaultMessage: '' },
  { id: 'save', defaultMessage: '' },
  { id: 'next', defaultMessage: '' },
  { id: 'enable', defaultMessage: '' },
  { id: 'disable', defaultMessage: '' },
  { id: 'edit', defaultMessage: '' },
  { id: 'copy', defaultMessage: '' },
  { id: 'print', defaultMessage: '' },
  { id: 'show', defaultMessage: '' },
  { id: 'download', defaultMessage: '' },
  { id: 'backHome', defaultMessage: '' },
  { id: 'add', defaultMessage: '' },
  { id: 'removeFilter', defaultMessage: '' },
  { id: 'back', defaultMessage: '' },
  { id: 'reset', defaultMessage: '' },
  { id: 'ok', defaultMessage: '' },
  { id: 'cancel', defaultMessage: '' },
  { id: 'sendAnonymously', defaultMessage: '' },
  { id: 'copied', defaultMessage: '' },
  { id: 'leaveQuestion', defaultMessage: '' },
] as const)

export const reportStatusMessages = defineMessages(
  'institution.reportStatus',
  Object.values(CaseStatusGroup).map(idToMessage)
)

export const reportSourceMessages = defineMessages(
  'institution.reportSource',
  Object.values(CaseSourceType).map(idToMessage)
)

export const reportPrioritiesMessages = defineMessages(
  'institution.reportPriority',
  Object.values(CasePriority).map(idToMessage)
)

export const reportDeadlineFilterMessages = defineMessages(
  'institution.reportDeadlineFilter',
  Object.values(CaseDeadlineFilter).map(idToMessage)
)

export const dayMessages = defineMessages('institution.day', Object.values(Day).map(idToMessage))

export const reportSourcesMessages = defineMessages(
  'institution.reportSources',
  Object.values(CaseSourceType).map(idToMessage)
)

export const reportJustificationMessages = defineMessages(
  'institution.reportJustification',
  Object.values(CaseJustification).map(idToMessage)
)

export const permissionTypeMessages = {
  ReportAccess: defineMessages('institution.permissionType.reportAccess', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  SettingsAccess: defineMessages('institution.permissionType.settingsAccess', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  BillingAccess: defineMessages('institution.permissionType.billingAccess', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  ManageCategories: defineMessages('institution.permissionType.manageCategories', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  ManageOrganizationalUnits: defineMessages(
    'institution.permissionType.manageOrganizationalUnits',
    [
      { id: 'title', defaultMessage: '' },
      { id: 'hint', defaultMessage: '' },
    ] as const
  ),
  ManageReportingChannels: defineMessages('institution.permissionType.manageReportingChannels', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  ManageUsers: defineMessages('institution.permissionType.manageUsers', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  Analytics: defineMessages('institution.permissionType.analytics', [
    { id: 'title', defaultMessage: '' },
  ] as const),
  EditCases: defineMessages('institution.permissionType.editCases', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  ExportCases: defineMessages('institution.permissionType.exportCases', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  DeleteCases: defineMessages('institution.permissionType.deleteCases', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  WriteInternalComments: defineMessages('institution.permissionType.writeInternalComments', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  WriteMessages: defineMessages('institution.permissionType.writeMessages', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
  Surveys: defineMessages('institution.permissionType.surveys', [
    { id: 'title', defaultMessage: '' },
  ] as const),
  InvestigationAccess: defineMessages('institution.permissionType.investigationAccess', [
    { id: 'title', defaultMessage: '' },
    { id: 'hint', defaultMessage: '' },
  ] as const),
} satisfies Record<FrontendPermissionType, { title: unknown }>
